import React, { useEffect, useState } from "react";
import { emailDraftApi } from "@/services/phase1Api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import { Check, Mail, RefreshCw, X } from "lucide-react";

export const EmailDraftsScreen = ({ session }) => {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [editedBody, setEditedBody] = useState("");
  const [editedSubject, setEditedSubject] = useState("");
  const [filter, setFilter] = useState("pending");

  const fetchDrafts = async () => {
    try {
      setLoading(true);
      const data = await emailDraftApi.list(filter === "all" ? null : filter, session);
      setDrafts(data);
    } catch (err) {
      console.error("Failed to fetch drafts:", err);
      toast.error("Failed to load email drafts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrafts();
  }, [filter, session]);

  const handleAction = async (action) => {
    if (!selectedDraft) return;
    try {
      await emailDraftApi.action(
        selectedDraft.id,
        action,
        action === "approve" ? editedBody : null,
        session,
        action === "approve" ? editedSubject : null,
      );
      toast.success(action === "approve" ? "Email approved" : "Email rejected");
      setSelectedDraft(null);
      setEditedBody("");
      setEditedSubject("");
      fetchDrafts();
    } catch (err) {
      console.error("Failed to process draft:", err);
      toast.error("Failed to process email draft");
    }
  };

  const openDraft = (draft) => {
    setSelectedDraft(draft);
    setEditedBody(draft.body);
    setEditedSubject(draft.subject);
  };

  return (
    <div className="w-full px-4 py-10 sm:px-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Email Drafts</h1>
          <p className="mt-1 text-sm text-foreground/60">
            AI-generated follow-up emails for your approval
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-10 w-[160px] rounded-none border-border bg-foreground/5 text-foreground">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent className="border-border bg-background text-foreground">
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Button
            onClick={fetchDrafts}
            variant="outline"
            className="h-10 rounded-none border-border bg-foreground/5 text-foreground hover:bg-foreground hover:text-background"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        {/* Drafts List */}
        <Card className="rounded-3xl border-border bg-foreground/5 p-4 backdrop-blur-2xl">
          <div className="px-2 py-3">
            <div className="text-sm font-semibold text-foreground">Drafts Queue</div>
            <div className="text-xs text-foreground/55">
              {drafts.length} draft{drafts.length !== 1 ? "s" : ""} {filter !== "all" ? filter : ""}
            </div>
          </div>
          
          <div className="max-h-[500px] space-y-2 overflow-auto">
            {loading ? (
              <div className="px-2 py-8 text-center text-sm text-foreground/60">
                Loading drafts...
              </div>
            ) : drafts.length === 0 ? (
              <div className="px-2 py-8 text-center text-sm text-foreground/60">
                No {filter !== "all" ? filter : ""} drafts found.
                <div className="mt-2 text-xs text-foreground/40">
                  Drafts are created when visitors chat with the AI and share their email.
                </div>
              </div>
            ) : (
              drafts.map((draft) => (
                <button
                  key={draft.id}
                  type="button"
                  onClick={() => openDraft(draft)}
                  className={[
                    "w-full rounded-xl border p-4 text-left transition-colors",
                    selectedDraft?.id === draft.id
                      ? "border-[var(--brand)]/50 bg-[var(--brand)]/10"
                      : "border-border bg-foreground/5 hover:bg-foreground/10",
                  ].join(" ")}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-foreground">
                        {draft.to_name || draft.to_email}
                      </div>
                      <div className="mt-1 truncate text-xs text-foreground/60">
                        {draft.subject}
                      </div>
                    </div>
                    <Badge
                      className={[
                        "shrink-0 rounded-full text-xs",
                        draft.status === "pending"
                          ? "bg-[#007a9e]/20 dark:bg-yellow-500/20 text-[#007a9e] dark:text-yellow-400"
                          : draft.status === "approved"
                          ? "bg-green-500/20 text-green-400"
                          : "bg-red-500/20 text-red-400",
                      ].join(" ")}
                    >
                      {draft.status}
                    </Badge>
                  </div>
                  <div className="mt-2 text-xs text-foreground/40">
                    {new Date(draft.created_at).toLocaleString()}
                  </div>
                </button>
              ))
            )}
          </div>
        </Card>

        {/* Draft Preview/Edit */}
        <Card className="rounded-3xl border-border bg-foreground/5 p-4 backdrop-blur-2xl">
          {selectedDraft ? (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold text-foreground">
                    Draft Preview
                  </div>
                  <div className="text-xs text-foreground/55">
                    Review and edit before approving
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedDraft(null);
                    setEditedBody("");
                  }}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border text-foreground/60 hover:bg-foreground/10"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <Separator className="bg-foreground/10" />

              <div className="space-y-3">
                <div>
                  <div className="text-xs uppercase tracking-wide text-foreground/55">
                    To
                  </div>
                  <div className="mt-1 text-sm text-foreground">
                    {selectedDraft.to_name ? `${selectedDraft.to_name} <${selectedDraft.to_email}>` : selectedDraft.to_email}
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wide text-foreground/55">
                    Subject
                  </div>
                  <Input
                    value={editedSubject}
                    onChange={(e) => setEditedSubject(e.target.value)}
                    disabled={selectedDraft.status !== "pending"}
                    className="mt-1 h-9 rounded-lg border-border bg-muted text-sm text-foreground"
                    data-testid="email-subject-input"
                  />
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wide text-foreground/55">
                    Body
                  </div>
                  <Textarea
                    value={editedBody}
                    onChange={(e) => setEditedBody(e.target.value)}
                    disabled={selectedDraft.status !== "pending"}
                    rows={12}
                    className="mt-1 rounded-xl border-border bg-muted text-sm text-foreground placeholder:text-foreground/35"
                  />
                </div>
              </div>

              {selectedDraft.status === "pending" && (
                <div className="flex items-center gap-2 pt-2">
                  <Button
                    onClick={() => handleAction("approve")}
                    className="flex-1 rounded-none bg-[var(--brand)] text-white hover:bg-[var(--brand)]"
                  >
                    <Check className="mr-2 h-4 w-4" />
                    Approve & Queue
                  </Button>
                  <Button
                    onClick={() => handleAction("reject")}
                    variant="outline"
                    className="flex-1 rounded-none border-border bg-foreground/5 text-foreground hover:bg-red-500/20 hover:text-red-400"
                  >
                    <X className="mr-2 h-4 w-4" />
                    Reject
                  </Button>
                </div>
              )}

              {selectedDraft.status !== "pending" && (
                <div className="rounded-xl bg-foreground/5 p-3 text-center text-sm text-foreground/60">
                  This draft has been {selectedDraft.status}.
                  {selectedDraft.approved_by && (
                    <span className="block text-xs text-foreground/40">
                      By {selectedDraft.approved_by} on{" "}
                      {new Date(selectedDraft.approved_at).toLocaleString()}
                    </span>
                  )}
                </div>
              )}

              <div className="text-xs text-foreground/40">
                Approved emails will be sent via the connected email service.
              </div>
            </div>
          ) : (
            <div className="flex h-[400px] flex-col items-center justify-center text-center">
              <Mail className="h-12 w-12 text-foreground/20" />
              <div className="mt-4 text-sm text-foreground/60">
                Select a draft to preview
              </div>
              <div className="mt-1 text-xs text-foreground/40">
                AI-generated emails appear here after visitors chat and share their email
              </div>
            </div>
          )}
        </Card>
      </div>

      <div className="mt-6 text-xs text-foreground/45">
        Email drafts are AI-generated. Connect an email service to enable sending.
      </div>
    </div>
  );
};
